import { FilterActionTypes, action, computed, thunk } from "easy-peasy";

import DEFAULT_WALK_IN_ENABLED from "./default";
import type { WalkInModel } from "./model.types";
import {
  DEFAULT_WALK_IN_TIME,
  getExpectedSessionTypes,
  isValidWalkInTime,
} from "./utils";
import { Logger } from "../../dependencies/logger";
import type { QueryVariables } from "../../gql/queries/allRestaurantIds";
import { createLogEffect } from "../../utils/store";
import getQuerySessions from "../session/getQuerySessions";

export type {
  WalkInClock,
  WalkInModel,
  WalkInQueryFilters,
} from "./model.types";

const logger = new Logger("RESTAURANT_STORE - WALK_IN", "LOG");
// logger.enable();

const logEffect = (property: keyof FilterActionTypes<WalkInModel>) =>
  createLogEffect<WalkInModel>("WALK_IN", property, false);

const walkInModel: WalkInModel = {
  enabled: DEFAULT_WALK_IN_ENABLED,
  enabledOnChange: logEffect("enabled"),
  setEnabled: action((state, enabled: boolean) => {
    logger.log("setEnabled", enabled);
    if (state.enabled === enabled) {
      return;
    }
    state.enabled = enabled;
  }),

  time: DEFAULT_WALK_IN_TIME,
  timeOnChange: logEffect("time"),

  clockRegionId: null,
  clockRegionIdOnChange: logEffect("clockRegionId"),

  setClock: action((state, { time, regionId }) => {
    logger.log("setClock", { time, regionId });
    if (!isValidWalkInTime(time)) {
      return;
    }
    if (state.time === time && state.clockRegionId === regionId) {
      return;
    }
    state.time = time;
    state.clockRegionId = regionId;
  }),

  expectedSessionTypes: computed([(state) => state.time], (time) =>
    getExpectedSessionTypes(time),
  ),
  expectedSessionTypesOnChange: logEffect("expectedSessionTypes"),

  activeSessionTypes: [],
  activeSessionTypesOnChange: logEffect("activeSessionTypes"),
  setActiveSessionTypes: action((state, sessions) => {
    logger.log("setActiveSessionTypes", sessions);
    if (
      state.activeSessionTypes.length === sessions.length &&
      state.activeSessionTypes.every(
        (session, index) => session === sessions[index],
      )
    ) {
      return;
    }
    state.activeSessionTypes = sessions;
  }),

  /**
   * Duplicate of First Table `onInputsChange` query assembly, except:
   * - no First Table `sessions` / `offersSession` / `offersFirstTableSession`
   * - `offersWalkInSession` scopes to venues offering walk-ins for the active session(s)
   * - `dates` is always the first entry of `discountDatesToShow`
   */
  buildQueryVariables: thunk((_actions, payload) => {
    const {
      regionId,
      suburbIds,
      favourite,
      tagIds,
      sort,
      people,
      status,
      discountDatesToShow,
      sessions,
    } = payload;

    const queryVariables: QueryVariables = {
      ids: favourite !== false && favourite.length > 0 ? favourite : undefined,
      regionId,
      suburbs: suburbIds,
      dates: [discountDatesToShow[0]],
      offersWalkInSession: getQuerySessions(sessions),
      tags: tagIds,
      sort,
      people,
      status,
    };

    logger.log("buildQueryVariables", queryVariables);
    return queryVariables;
  }),
};

export default walkInModel;
